// Verified-buyer product reviews. The DB trigger
// reviews_validate_purchase guarantees that submitReview only succeeds
// when the caller actually owns the referenced order_item and the parent
// order is in a fulfilment state — so this helper just round-trips the
// payload and surfaces any error.

import { supabase } from './supabase.js';
import { getSessionUser } from './auth.js';

/**
 * Public list of approved reviews for a product. Newest first.
 */
export async function fetchProductReviews(productId) {
  const pid = parseInt(productId, 10);
  if (!Number.isInteger(pid)) return { data: [], error: null };
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('product_id', pid)
    .eq('status', 'approved')
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

/**
 * Aggregate { count, avg } across approved reviews for a product. The
 * products table already carries denormalised rating + reviews columns
 * (kept current by a DB trigger) so most call sites can read straight
 * from that. This helper exists for places that want a fresher number
 * — e.g. immediately after the customer submits a review.
 */
export async function fetchProductReviewSummary(productId) {
  const pid = parseInt(productId, 10);
  if (!Number.isInteger(pid)) return { count: 0, avg: 0, error: null };
  const { data, error } = await supabase
    .from('reviews')
    .select('rating')
    .eq('product_id', pid)
    .eq('status', 'approved');
  if (error) return { count: 0, avg: 0, error };
  const rows = data || [];
  const count = rows.length;
  const avg = count
    ? Math.round((rows.reduce((s, r) => s + r.rating, 0) / count) * 10) / 10
    : 0;
  return { count, avg, error: null };
}

/**
 * Submit a review. Caller passes the order_item_id they're reviewing —
 * the trigger validates ownership + fulfilment state and rejects the
 * insert otherwise.
 *
 * @param {Object} input
 * @param {string} input.orderItemId — uuid of the order_items row
 * @param {number} input.productId   — must match the order_item's product_id
 * @param {number} input.rating      — 1..5
 * @param {string} [input.title]
 * @param {string} [input.body]
 */
export async function submitReview({ orderItemId, productId, rating, title, body }) {
  const user = await getSessionUser();
  if (!user) return { data: null, error: new Error('Sign in to leave a review.') };

  const r = parseInt(rating, 10);
  if (!Number.isInteger(r) || r < 1 || r > 5) {
    return { data: null, error: new Error('Rating must be between 1 and 5.') };
  }

  const payload = {
    product_id:    parseInt(productId, 10),
    user_id:       user.id,
    order_item_id: orderItemId,
    rating:        r,
    title:         title && title.trim() ? title.trim().slice(0, 120) : null,
    body:          body && body.trim()  ? body.trim().slice(0, 4000) : null,
    // Default status comes from the DB ('approved'); admin can later hide
    // a row from the moderation tab if needed.
  };

  const { data, error } = await supabase
    .from('reviews')
    .insert(payload)
    .select()
    .single();
  return { data, error };
}

/**
 * Reviews authored by the current user, with the linked product_id —
 * used by My Orders to mark order line items as "already reviewed".
 */
export async function fetchMyReviewedOrderItemIds() {
  const user = await getSessionUser();
  if (!user) return { ids: [], error: null };
  const { data, error } = await supabase
    .from('reviews')
    .select('order_item_id')
    .eq('user_id', user.id);
  return { ids: (data || []).map(r => r.order_item_id), error };
}
